import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Check, ChevronLeft } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { taskService } from '@/services/taskService'
import TaskTypeIcon from './TaskTypeIcon'
import { TaskSearchList } from './TaskPicker'

const levelOf = (type) => type?.hierarchy_level ?? 0

const TaskTypeSwitcher = ({ taskId, projectId, projectKey, currentType, children }) => {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  // Turning a task into a sub-task needs its parent task first, like Jira's "convert to sub-task"
  const [subtaskType, setSubtaskType] = useState(null)

  const { data: typesData } = useQuery(
    'task-types',
    () => taskService.getTaskTypes(),
    { staleTime: 60000 * 5 }
  )

  const types = typesData?.data?.types || []

  const handleOpenChange = (next) => {
    setOpen(next)
    if (!next) setSubtaskType(null)
  }

  const save = async (typeId, parentId) => {
    try {
      await taskService.updateTask(projectId, taskId, { type_id: typeId, ...(parentId && { parent_id: parentId }) })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      toast.success('Task type updated')
      handleOpenChange(false)
    } catch (err) {
      // e.g. "This task has 2 child item(s) that can't stay under that type..."
      toast.error(err.response?.data?.error || 'Failed to update task type')
    }
  }

  const handleSelect = (type) => {
    if (type.id === currentType?.id) return
    if (levelOf(type) === -1 && levelOf(currentType) !== -1) {
      setSubtaskType(type)
      return
    }
    save(type.id)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className={subtaskType ? 'w-80 p-2' : 'w-40 p-1'} align="start">
        {subtaskType ? (
          <>
            <button
              type="button"
              onClick={() => setSubtaskType(null)}
              className="flex items-center gap-1 px-1 pb-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Pick the task this sub-task belongs to
            </button>
            <TaskSearchList
              projectId={projectId}
              projectKey={projectKey}
              filter={(t) => t.id !== taskId && levelOf(t.type) === 0}
              onSelect={(t) => save(subtaskType.id, t.id)}
            />
          </>
        ) : types.map((type) => (
          <button
            key={type.id}
            onClick={() => handleSelect(type)}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent transition-colors text-left"
          >
            <TaskTypeIcon type={type} size="sm" />
            <span className="flex-1">{type.name}</span>
            {type.id === currentType?.id && (
              <Check className="h-3.5 w-3.5 text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export default TaskTypeSwitcher
